"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import type { WMenuContent, WMenuItem } from "@/lib/api/cms-window-content";
import { useT } from "@/lib/i18n/context";
import { wMenuTranslations } from "./WMenu.i18n";
import styles from "./WMenu.module.css";

/**
 * WMenu — komponent renderujący menu (typ `w_menu`).
 *
 * **Data flow:**
 *   - Server-side fetcha `fetchWindowContent<WMenuContent>(id)` z REST API
 *   - Backend `W_MENU_CONTROLLER::getApiContent()` zwraca CAŁE drzewo katalogów
 *   - Ten komponent renderuje HTML/CSS z prop'ów, decyduje co pokazać per
 *     aktualną ścieżkę URL.
 *
 * **Active state — frontend-computed:** porównuje `usePathname()` z `url`
 * każdego item'a:
 *   - `isActive` — exact match (item to aktualna strona)
 *   - `isInActivePath` — bieżąca strona jest pod URL'em item'a (przodek w drzewie)
 *
 * **Progressive disclosure — kolejne poziomy pojawiają się PO KLIKNIĘCIU rodzica:**
 *   - Top-level: ZAWSZE pokazane
 *   - Children item'a są renderowane GDY ten item jest `isActive` lub
 *     `isInActivePath` (czyli user kliknął go lub jego potomka — co fizycznie
 *     zawsze oznacza nawigację, bo każdy menu item to `<a href>` do katalogu).
 *   - Wnuki: NIE pokazujemy automatycznie — pojawią się dopiero gdy user
 *     kliknie dziecko i wejdzie głębiej. Każdy poziom = jedno kliknięcie.
 *   - Inne gałęzie (sibling'i aktywnej ścieżki) — children ukryte.
 *
 * Wzorzec spójny z legacy Imperial (`parseNodesRecursive` + URL params), ale
 * realizowany 100% client-side bo backend zwraca full tree (cache friendly).
 */
export function WMenu({ content, expandAll = false }: WMenuProps) {
  const currentPathname = usePathname() ?? "";
  const t = useT(wMenuTranslations);

  if (!content || content.items.length === 0) {
    return (
      <nav className={styles.empty} aria-label={content?.title ?? t.ariaLabelFallback}>
        {t.empty}
      </nav>
    );
  }

  return (
    <nav className={styles.menu} aria-label={content.title}>
      <MenuList
        items={content.items}
        level={1}
        currentPathname={currentPathname}
        expandAll={expandAll}
      />
    </nav>
  );
}

export type WMenuProps = {
  content: WMenuContent | null;
  /**
   * Gdy `true` — całe drzewo renderowane jest rozwinięte (wszystkie poziomy),
   * niezależnie od aktywnej ścieżki. Domyślnie `false` (progressive disclosure:
   * children pokazywane tylko na aktywnej ścieżce).
   *
   * Używane przez `w_menu_selected_catalogs` (kuratorowane poddrzewo katalogów
   * w sidebarze) — legacy zachowanie tego okna pokazywało pełne poddrzewo.
   * Głębokość i tak jest przycięta przez backend (`max_dep_level`).
   */
  expandAll?: boolean;
};

function MenuList({
  items,
  level,
  currentPathname,
  expandAll,
}: {
  items: WMenuItem[];
  level: number;
  currentPathname: string;
  expandAll: boolean;
}) {
  return (
    <ul className={styles.list} data-level={level}>
      {items.map((item) => (
        <MenuItem
          key={item.id}
          item={item}
          level={level}
          currentPathname={currentPathname}
          expandAll={expandAll}
        />
      ))}
    </ul>
  );
}

function MenuItem({
  item,
  level,
  currentPathname,
  expandAll,
}: {
  item: WMenuItem;
  level: number;
  currentPathname: string;
  expandAll: boolean;
}) {
  const hasChildren = item.children.length > 0;
  const { isActive, isInActivePath } = computeActiveState(item.url, currentPathname);

  /* Render direct children TYLKO gdy ten item jest aktywny lub jest
     ancestor'em aktywnego (czyli user kliknął go lub przeszedł głębiej).
     Wnuki sterowane przez to samo prawo per własny isActive/isInActivePath —
     żeby je zobaczyć, user musi kliknąć dziecko (= przejść do katalogu).
     `expandAll` (w_menu_selected_catalogs) wymusza pełne rozwinięcie. */
  const showChildren = hasChildren && (expandAll || isActive || isInActivePath);

  const linkClass = [
    styles.link,
    isActive ? styles.linkActive : "",
    !isActive && isInActivePath ? styles.linkInActivePath : "",
  ]
    .filter(Boolean)
    .join(" ");

  /* Client-side navigation: użycie `<Link>` z next/link zamiast `<a>` —
     przejścia między podstronami bez full page reload (zachowane state,
     skeleton z `loading.tsx`, streamowane okna).
     `<Link>` używamy TYLKO dla internal links bez `target` — zewnętrzne URL'e
     z `target="_blank"` wymagają natywnego `<a>` (Link nie wspiera dobrze new tab).
     URL z backend (`ILINK::getCatalogUrl`) bywa pełny — `extractInternalHref`
     wyciąga sam pathname dla bezpieczeństwa Link'a. */
  const internalHref = extractInternalHref(item.url, item.target, item.type);

  return (
    <li className={styles.item}>
      {internalHref !== null ? (
        <Link
          href={internalHref}
          className={linkClass}
          aria-current={isActive ? "page" : undefined}
        >
          {item.name}
        </Link>
      ) : (
        <a
          href={item.url}
          target={item.target ?? undefined}
          rel={item.target === "_blank" ? "noopener noreferrer" : undefined}
          className={linkClass}
          aria-current={isActive ? "page" : undefined}
        >
          {item.name}
        </a>
      )}
      {showChildren && (
        <MenuList
          items={item.children}
          level={level + 1}
          currentPathname={currentPathname}
          expandAll={expandAll}
        />
      )}
    </li>
  );
}

/**
 * Wyciąga pathname (+ optional search/hash) z URL'a backendu dla `<Link>`.
 * Zwraca `null` dla URL'i które wymagają natywnego `<a>` (cross-origin,
 * new-tab, nieparsable schematy).
 *
 * Backend `ILINK::getCatalogUrl()` typowo zwraca PEŁNY URL z hostem (z config
 * domeny CMS frontendu). Dla `<Link>` wystarczy nam path — łatwiej + bezpieczniej
 * niż pełen URL (Link nie zawsze poprawnie obsługuje full URLs).
 *
 * **Reguły (deterministyczne SSR/client — żeby nie było hydration mismatch'a):**
 *   - `target` ustawione (`_blank`, `_self` itp.) -> `null` (użyj `<a>` — admin
 *     explicit oznaczył jako external / new-tab)
 *   - URL relatywny (`/pl/foo`) -> as-is
 *   - URL absolutny http(s) -> wyciągamy `pathname + search + hash`
 *     (zakładamy że bez `target` URL wskazuje na bieżącą domenę CMS —
 *     menu items dla innych domen powinny mieć `target` set'owany przez
 *     admina w IADMIN; jeśli admin tego nie zrobi, link kliknięty w SPA-mode
 *     spróbuje przejść na ten path na BIEŻĄCEJ domenie i da 404)
 *   - URL z innym schematem (`mailto:`, `tel:`, `javascript:`) -> `null`
 *
 * **SSR-safe:** NIE używamy `window.location.host` (niedostępny server-side
 * + powodowałby hydration mismatch) — decyzja Link vs `<a>` jest taka sama
 * podczas SSR i client render.
 */
function extractInternalHref(rawUrl: string, target: string | null, type: number): string | null {
  if (!rawUrl) return null;
  /* type=4 (`ILINK_CATALOG_LINK` w PHP) — `url` to wartość `imp_cms_catalog.link`,
     wskazująca na ZEWNĘTRZNY adres www (np. `http://www.agendo.pl/`). Nawet
     gdy admin nie ustawił `target=_blank`, NIE wolno opakować w `<Link>`, bo
     pathname extraction poniżej wyciągnęłaby sam `/` i link szedłby na
     bieżącą domenę. */
  if (type === 4) return null;
  /* `target` ustawione -> external / new-tab -> `<a>`. */
  if (target) return null;

  /* Relatywny path — zawsze internal. */
  if (rawUrl.startsWith("/")) return rawUrl;

  /* Absolutny URL — sparsuj i wyciągnij path. Tylko http(s) traktujemy jako
     potencjalnie internal — inne schematy (`mailto:`, `tel:`) zostają w `<a>`. */
  try {
    const parsed = new URL(rawUrl);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return null;
    return parsed.pathname + parsed.search + parsed.hash;
  } catch {
    /* Nieparsable URL — fallback do `<a>`. */
    return null;
  }
}

/**
 * Compute `isActive` (exact match) + `isInActivePath` (ancestor) dla menu item'a.
 *
 * `itemUrl` może być pełnym URL'em (z hostem, generowanym przez backend ILINK)
 * lub relatywnym path'em. Wyciągamy `pathname` przez `new URL(...)` z fallback'iem
 * dla relatywnych. Następnie normalizujemy (strip trailing slash) i porównujemy.
 *
 * **Active:** `currentPathname === itemPath` (dokładny match)
 * **In active path:** `currentPathname` zaczyna się od `itemPath + "/"` (parent
 * w drzewie nawigacji — np. menu item `/pl/sklep` jest "w ścieżce" gdy admin
 * jest na `/pl/sklep/odziez`).
 */
function computeActiveState(
  itemUrl: string,
  currentPathname: string,
): { isActive: boolean; isInActivePath: boolean } {
  let itemPath: string;
  try {
    /* Pełen URL — wyciągamy pathname. */
    itemPath = new URL(itemUrl).pathname;
  } catch {
    /* Relatywny — używamy bezpośrednio. */
    itemPath = itemUrl;
  }
  /* Normalizacja — usuwamy trailing slash dla spójności (Next.js usePathname
     zwraca bez trailing slash, a backend ILINK może generować z). */
  const normalize = (p: string) => (p.length > 1 && p.endsWith("/") ? p.slice(0, -1) : p);
  const item = normalize(itemPath);
  const current = normalize(currentPathname);

  const isActive = current === item;
  const isInActivePath = !isActive && current.startsWith(item + "/");

  return { isActive, isInActivePath };
}
